import type {
  BillDetail,
  BillVote,
  CommitteeDetail,
  FecCommitteeProfile,
  HouseRollCallVoteContext,
  MeetingDetail,
  MemberDetail,
  SenateFloorVoteItem,
} from "./types";
import { getBillStatusLabel } from "./bill-progress";
import {
  formatSenateFloorVoteReference,
  normalizeCommitteeRouteChamber,
  resolveSenateFloorVoteReference,
} from "./utils";

export const SITE_NAME = "Govoptes";
export const SITE_ALTERNATE_NAME = "Federal Monitor";
export const SITE_TAGLINE = "Congress, made legible";
export const HOME_HERO_TITLE = "Congress, made legible.";
export const HOME_HERO_DESCRIPTION =
  "Browse public summaries of bills, members, committees, meetings, and votes, then use a Govoptes account to monitor the congressional activity that matters to you.";
export const DEFAULT_DESCRIPTION = HOME_HERO_DESCRIPTION;
export const SOCIAL_IMAGE_PATH = "/social-card.png";
export const MEMBER_PORTRAIT_SOCIAL_IMAGE_PATH = "/_social/member-portrait";
export const INDEX_ROBOTS = "index,follow";
export const NOINDEX_ROBOTS = "noindex,follow";

const DEFAULT_SOCIAL_HEADLINE = "Congress, made legible";
const DEFAULT_SOCIAL_SUPPORTING_LINE =
  "Track bills, members, committees, and votes in one clear view.";

type BillSeoInput = Pick<
  BillDetail["bill"],
  | "billId"
  | "billNumber"
  | "billType"
  | "citation"
  | "congress"
  | "introducedDate"
  | "latestActionDate"
  | "latestActionText"
  | "lawNumber"
  | "lawType"
  | "originChamber"
  | "policyArea"
  | "sponsorName"
  | "status"
  | "subjects"
  | "summary"
  | "title"
>;
type VoteSeoInput = Pick<
  BillVote,
  | "chamber"
  | "nayCount"
  | "otherCount"
  | "otherLabel"
  | "partyBreakdown"
  | "presentCount"
  | "question"
  | "result"
  | "rollNumber"
  | "title"
  | "voteDate"
  | "yeaCount"
>;
type CommitteeSeoInput = Pick<
  CommitteeDetail["committee"],
  | "billCount"
  | "chamber"
  | "congress"
  | "memberCount"
  | "name"
  | "subcommitteeCount"
  | "systemCode"
  | "upcomingMeetingCount"
>;
type MemberSeoInput = Pick<
  MemberDetail["member"],
  | "bioguideId"
  | "chamber"
  | "displayName"
  | "district"
  | "officialWebsiteUrl"
  | "partyCode"
  | "portraitUrl"
  | "stateCode"
>;
type FecCommitteeSeoInput = Pick<
  FecCommitteeProfile,
  "committeeId" | "committeeLabel" | "name"
>;
type MeetingSeoInput = Pick<
  MeetingDetail["meeting"],
  | "chamber"
  | "committees"
  | "congress"
  | "meetingDate"
  | "meetingId"
  | "meetingStatus"
  | "title"
>;
type HouseVoteSeoInput = Pick<
  HouseRollCallVoteContext,
  "citation" | "congress" | "linkedBill" | "rollNumber" | "sessionNumber"
>;
type SenateVoteSeoInput = Pick<
  SenateFloorVoteItem,
  | "bill"
  | "congress"
  | "issue"
  | "reference"
  | "result"
  | "sessionNumber"
  | "title"
  | "voteDate"
  | "voteNumber"
>;

export type SeoRoute =
  | { kind: "home" }
  | { kind: "about" }
  | { kind: "legal" }
  | { kind: "privacy" }
  | { kind: "terms" }
  | { kind: "contact" }
  | { kind: "insights" }
  | { kind: "congress-explained" }
  | {
      kind: "congress-explainer";
      slug: string;
      title: string;
      description: string;
    }
  | { kind: "insight-how-a-bill-becomes-law" }
  | { kind: "ai-legislation-insight" }
  | { kind: "cloture-insight" }
  | { kind: "reintroduced-bills-insight" }
  | { kind: "reported-by-committee-insight" }
  | { kind: "money-influence" }
  | { kind: "outside-spending-insight" }
  | { kind: "pac-vs-super-pac-insight" }
  | { kind: "monitor" }
  | { kind: "account-settings" }
  | { kind: "notifications-settings" }
  | { kind: "pricing" }
  | { kind: "sign-in" }
  | { kind: "sign-up" }
  | { kind: "forgot-password" }
  | { kind: "reset-password" }
  | { kind: "welcome" }
  | { kind: "welcome-access" }
  | { kind: "bills"; search?: string }
  | {
      kind: "bill-detail";
      congress?: number | string;
      billType?: string;
      billNumber?: string;
      bill?: BillSeoInput | null;
    }
  | {
      kind: "bill-vote-detail";
      congress?: number | string;
      billType?: string;
      billNumber?: string;
      voteId?: string;
      bill?: BillSeoInput | null;
      vote?: VoteSeoInput | null;
    }
  | {
      kind: "house-vote-detail";
      congress?: number | string;
      sessionNumber?: number | string;
      rollNumber?: number | string;
      houseVote?: HouseVoteSeoInput | null;
      vote?: VoteSeoInput | null;
    }
  | {
      kind: "committee-detail";
      chamber?: string;
      committeeCode?: string;
      congress?: number | string;
      committee?: CommitteeSeoInput | null;
    }
  | {
      kind: "fec-committee-detail";
      committeeId?: string;
      committee?: FecCommitteeSeoInput | null;
    }
  | { kind: "members"; search?: string }
  | {
      kind: "member-detail";
      bioguideId?: string;
      congress?: number | string;
      currentCongress?: number | string;
      member?: MemberSeoInput | null;
    }
  | { kind: "schedule"; search?: string }
  | {
      kind: "meeting-detail";
      chamber?: string;
      congress?: number | string;
      eventId?: string;
      meeting?: MeetingSeoInput | null;
    }
  | {
      kind: "senate-vote-detail";
      congress?: number | string;
      sessionNumber?: number | string;
      voteNumber?: number | string;
      vote?: VoteSeoInput | null;
      senateVote?: SenateVoteSeoInput | null;
    }
  | { kind: "fallback"; pathname?: string };

export type SeoDescriptor = {
  applicationName: string;
  canonicalUrl: string;
  description: string;
  jsonLd: Record<string, unknown> | null;
  openGraph: {
    description: string;
    imageAlt: string;
    imageHeight: number;
    imageType: "image/jpeg" | "image/png";
    imageUrl: string;
    imageWidth: number;
    siteName: string;
    title: string;
    type: "website";
    url: string;
  };
  robots: string;
  socialCard: SocialCardDescriptor;
  title: string;
  twitter: {
    card: "summary_large_image";
    description: string;
    imageAlt: string;
    imageUrl: string;
    title: string;
  };
};

export type SocialCardVariant =
  "home" | "bill" | "vote" | "member" | "committee" | "generic";

export type SocialCardMetric = {
  label: string;
  value: string;
};

export type SocialCardTally = {
  yea: number | null;
  nay: number | null;
  present: number | null;
  other: number | null;
  otherLabel: string;
};

export type SocialCardDescriptor = {
  badge?: string;
  detailLine?: string;
  eyebrow?: string;
  headline: string;
  imageAlt: string;
  metrics?: SocialCardMetric[];
  portraitUrl?: string | null;
  supportingLine: string;
  tally?: SocialCardTally;
  variant: SocialCardVariant;
};

type BaseSeoDescriptor = {
  description: string;
  path: string;
  robots: string;
  structuredData?: Record<string, unknown> | StructuredDataFactory | null;
  title: string;
};

type StructuredDataContext = {
  canonicalUrl: string;
  description: string;
  path: string;
  siteUrl: string;
  title: string;
};

type StructuredDataFactory = (
  context: StructuredDataContext,
) => Record<string, unknown>;

export function normalizeSiteUrl(
  candidate: string | null | undefined,
  fallbackUrl: string,
): string {
  const fallback = normalizeBaseUrl(fallbackUrl);
  if (!candidate) {
    return fallback;
  }

  try {
    return normalizeBaseUrl(candidate);
  } catch {
    return fallback;
  }
}

export function buildSeoDescriptor(
  route: SeoRoute,
  siteUrl: string,
): SeoDescriptor {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl, "http://localhost");
  const baseDescriptor = buildBaseSeoDescriptor(route, normalizedSiteUrl);
  const canonicalUrl = new URL(
    baseDescriptor.path,
    `${normalizedSiteUrl}/`,
  ).toString();
  const socialCard = buildSocialCardDescriptor(route);
  const socialImage = buildSocialImage(route, baseDescriptor.path, normalizedSiteUrl);
  const title = clamp(baseDescriptor.title, 70);
  const description = clamp(baseDescriptor.description, 170);
  const jsonLd = resolveStructuredData(baseDescriptor.structuredData, {
    canonicalUrl,
    description,
    path: baseDescriptor.path,
    siteUrl: normalizedSiteUrl,
    title,
  });

  return {
    applicationName: SITE_NAME,
    canonicalUrl,
    description,
    jsonLd,
    openGraph: {
      description,
      imageAlt: socialCard.imageAlt,
      imageHeight: socialImage.height,
      imageType: socialImage.type,
      imageUrl: socialImage.url,
      imageWidth: socialImage.width,
      siteName: SITE_NAME,
      title,
      type: "website",
      url: canonicalUrl,
    },
    robots: baseDescriptor.robots,
    socialCard,
    title,
    twitter: {
      card: "summary_large_image",
      description,
      imageAlt: socialCard.imageAlt,
      imageUrl: socialImage.url,
      title,
    },
  };
}

export function buildSocialCardDescriptor(
  route: SeoRoute,
): SocialCardDescriptor {
  switch (route.kind) {
    case "home":
      return createDefaultSocialCard("home");
    case "bill-detail":
      if (route.bill) {
        return {
          badge: getBillStatusLabel(
            route.bill.status,
            route.bill.originChamber,
          ),
          detailLine: route.bill.latestActionText
            ? clamp(route.bill.latestActionText, 120)
            : formatBillSponsorLine(route.bill),
          eyebrow: [route.bill.originChamber, route.bill.policyArea]
            .filter(Boolean)
            .join(" · "),
          headline: route.bill.citation,
          imageAlt: `Govoptes bill page for ${route.bill.citation}`,
          supportingLine: clamp(route.bill.title, 100),
          variant: "bill",
        };
      }
      return createDefaultSocialCard("generic");
    case "bill-vote-detail":
      if (route.bill || route.vote) {
        return createVoteSocialCard({
          billLabel: route.bill?.citation ?? "Bill vote",
          subject:
            route.vote?.question ??
            route.vote?.title ??
            route.bill?.title ??
            "Recorded vote",
          vote: route.vote ?? null,
        });
      }
      return createDefaultSocialCard("generic");
    case "house-vote-detail":
      if (route.houseVote || route.vote) {
        return createVoteSocialCard({
          billLabel:
            route.houseVote?.linkedBill?.citation ??
            route.houseVote?.citation ??
            `House Roll ${route.rollNumber ?? ""}`.trim(),
          subject:
            route.vote?.question ??
            route.vote?.title ??
            route.houseVote?.linkedBill?.title ??
            "House roll call",
          vote: route.vote ?? null,
        });
      }
      return createDefaultSocialCard("generic");
    case "senate-vote-detail":
      if (route.senateVote || route.vote) {
        const reference = route.senateVote
          ? resolveSenateFloorVoteReference(route.senateVote)
          : null;
        return createVoteSocialCard({
          billLabel:
            route.senateVote?.bill?.billCitation ??
            (route.senateVote
              ? `Senate Vote ${route.senateVote.voteNumber}`
              : `Senate Vote ${route.voteNumber ?? ""}`.trim()),
          fallbackDate: route.senateVote?.voteDate ?? null,
          fallbackResult: route.senateVote?.result ?? null,
          subject:
            route.vote?.question ??
            route.vote?.title ??
            reference?.title ??
            route.senateVote?.issue ??
            "Senate roll call",
          vote: route.vote ?? null,
        });
      }
      return createDefaultSocialCard("generic");
    case "member-detail":
      if (route.member) {
        return {
          detailLine: describeMemberRole(route.member),
          headline: route.member.displayName,
          imageAlt: `Govoptes member profile for ${route.member.displayName}`,
          portraitUrl: route.member.portraitUrl,
          supportingLine: formatMemberSocialSupportingLine(route.member),
          variant: "member",
        };
      }
      return createDefaultSocialCard("generic");
    case "committee-detail":
      if (route.committee) {
        const headline = formatCommitteeDisplayName(route.committee.name);
        return {
          headline,
          imageAlt: `Govoptes committee page for ${headline}`,
          supportingLine: formatCommitteeSocialSupportingLine(route.committee),
          variant: "committee",
        };
      }
      return createDefaultSocialCard("generic");
    case "fec-committee-detail":
      if (route.committee) {
        return {
          headline: route.committee.committeeLabel,
          imageAlt: `Govoptes outside spending group page for ${route.committee.committeeLabel}`,
          supportingLine: clamp(route.committee.name, 100),
          variant: "committee",
        };
      }
      return createDefaultSocialCard("generic");
    default:
      return createDefaultSocialCard("generic");
  }
}

function buildBaseSeoDescriptor(
  route: SeoRoute,
  siteUrl: string,
): BaseSeoDescriptor {
  switch (route.kind) {
    case "home":
      return {
        description: DEFAULT_DESCRIPTION,
        path: "/",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `${SITE_NAME} | ${SITE_TAGLINE}`,
      };
    case "about":
      return {
        description:
          "Govoptes helps you track bills, members, committees, and floor activity in one place so you can follow Congress without stitching together multiple government websites.",
        path: "/about",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `About ${SITE_NAME} | Congress tracker`,
      };
    case "legal":
      return {
        description: `Read ${SITE_NAME} legal policies, including privacy and terms for accounts, subscriptions, and service use.`,
        path: "/legal",
        robots: INDEX_ROBOTS,
        title: `Legal | ${SITE_NAME}`,
      };
    case "privacy":
      return {
        description: `Read how ${SITE_NAME} handles account, billing, alert, and support information.`,
        path: "/privacy",
        robots: INDEX_ROBOTS,
        title: `Privacy Policy | ${SITE_NAME}`,
      };
    case "terms":
      return {
        description: `Read the terms that govern access to ${SITE_NAME}, including subscriptions and acceptable use.`,
        path: "/terms",
        robots: INDEX_ROBOTS,
        title: `Terms of Service | ${SITE_NAME}`,
      };
    case "contact":
      return {
        description: `Contact ${SITE_NAME} for support, billing questions, account issues, bug reports, or product feedback.`,
        path: "/contact",
        robots: INDEX_ROBOTS,
        title: `Contact | ${SITE_NAME}`,
      };
    case "pricing":
      return {
        description: `${SITE_NAME} pricing for Free, Analyst, Team Workspace, and Enterprise Workspace congressional monitoring workflows.`,
        path: "/pricing",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `Pricing | ${SITE_NAME}`,
      };
    case "insights":
      return {
        description: `Source-grounded analysis connected to live bills, votes, members, committees, and hearings from ${SITE_NAME}.`,
        path: "/insights",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `Insights | ${SITE_NAME}`,
      };
    case "congress-explained":
      return {
        description:
          "Plain-English guides to congressional procedure, connected to live bills and official action records.",
        path: "/insights/congress-explained",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `Congress Explained | ${SITE_NAME}`,
      };
    case "congress-explainer":
      return {
        description: route.description,
        path: `/insights/${route.slug}`,
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `${route.title} | ${SITE_NAME}`,
      };
    case "insight-how-a-bill-becomes-law":
      return {
        description: `Learn how a bill becomes a law, from introduction and committee markup through floor votes, reconciliation, and presidential action, with live examples from ${SITE_NAME}.`,
        path: "/insights/how-a-bill-becomes-law",
        robots: INDEX_ROBOTS,
        structuredData: ({ canonicalUrl }) => ({
          "@context": "https://schema.org",
          "@type": "Article",
          dateModified: "2026-07-12",
          datePublished: "2026-07-08",
          description:
            "A step-by-step guide to the federal legislative process with live bill and committee examples.",
          headline: "How a Bill Becomes a Law",
          mainEntityOfPage: canonicalUrl,
          publisher: { "@type": "Organization", name: SITE_NAME },
        }),
        title: `How a Bill Becomes a Law: 12 Steps | ${SITE_NAME}`,
      };
    case "ai-legislation-insight":
      return {
        description:
          "Congress is considering artificial intelligence from nearly every angle. See the federal AI bills shaping the debate, which measures are moving, and what to watch next.",
        path: "/insights/federal-ai-legislation",
        robots: INDEX_ROBOTS,
        structuredData: ({ canonicalUrl }) => ({
          "@context": "https://schema.org",
          "@type": "Article",
          author: { "@type": "Organization", name: `${SITE_NAME} Insights` },
          dateModified: "2026-07-12",
          datePublished: "2026-07-12",
          description:
            "A live, source-grounded analysis of the federal artificial intelligence bills shaping the current Congress.",
          headline:
            "The Federal AI Bills Shaping the Debate—and Which Ones Are Actually Moving",
          image: `${siteUrl}/insights/ai-policy-feature-v2.webp`,
          mainEntityOfPage: canonicalUrl,
          publisher: { "@type": "Organization", name: SITE_NAME },
        }),
        title: `Federal AI Legislation: Bills and Momentum | ${SITE_NAME}`,
      };
    case "cloture-insight":
      return {
        description:
          "What cloture means in the U.S. Senate, why it usually takes 60 votes, and live examples of bills with cloture filed, invoked, or rejected.",
        path: "/insights/what-is-cloture",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `What Is Cloture? Senate Rule XXII Explained | ${SITE_NAME}`,
      };
    case "reintroduced-bills-insight":
      return {
        description:
          "Why bills expire at the end of a Congress, what resets, and what reintroduction signals—with live examples from current congressional records.",
        path: "/insights/why-bills-get-reintroduced",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `Why Bills Get Reintroduced Every Congress | ${SITE_NAME}`,
      };
    case "reported-by-committee-insight":
      return {
        description:
          "What reported by committee means, how markup and committee reports work, and live examples of bills advancing from House and Senate committees.",
        path: "/insights/reported-by-committee",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `What Does Reported by Committee Mean? | ${SITE_NAME}`,
      };
    case "money-influence":
      return {
        description:
          "Campaign-finance explainers connected to current FEC independent-expenditure records, disclosure rules, PACs, Super PACs, and outside spending.",
        path: "/insights/money-and-influence",
        robots: INDEX_ROBOTS,
        structuredData: buildStructuredData(siteUrl),
        title: `Money & Influence: Campaign Finance Explained | ${SITE_NAME}`,
      };
    case "outside-spending-insight":
      return {
        description:
          "What outside spending and independent expenditures mean, who reports them, how support and opposition appear in FEC data, and what disclosure can leave hidden.",
        path: "/insights/outside-spending",
        robots: INDEX_ROBOTS,
        structuredData: ({ canonicalUrl }) => ({
          "@context": "https://schema.org",
          "@type": "Article",
          author: { "@type": "Organization", name: `${SITE_NAME} Insights` },
          dateModified: "2026-07-13",
          datePublished: "2026-07-13",
          description:
            "A source-grounded guide to outside spending, independent expenditures, electioneering communications, disclosure, and coordination.",
          headline: "What Is Outside Spending? Independent Expenditures, Explained",
          mainEntityOfPage: canonicalUrl,
          publisher: { "@type": "Organization", name: SITE_NAME },
        }),
        title: `What Is Outside Spending? Independent Expenditures Explained | ${SITE_NAME}`,
      };
    case "pac-vs-super-pac-insight":
      return {
        description:
          "PAC vs. Super PAC explained: contribution limits, candidate giving, independent expenditures, corporate and labor funds, donor disclosure, and current FEC data.",
        path: "/insights/pac-vs-super-pac",
        robots: INDEX_ROBOTS,
        structuredData: ({ canonicalUrl }) => ({
          "@context": "https://schema.org",
          "@type": "Article",
          author: { "@type": "Organization", name: `${SITE_NAME} Insights` },
          dateModified: "2026-07-13",
          datePublished: "2026-07-13",
          description:
            "A current guide to the legal and reporting differences between traditional PACs and Super PACs.",
          headline: "What Is a Super PAC vs. a PAC?",
          mainEntityOfPage: canonicalUrl,
          publisher: { "@type": "Organization", name: SITE_NAME },
        }),
        title: `Super PAC vs. PAC: What's the Difference? | ${SITE_NAME}`,
      };
    case "monitor":
      return {
        description: `Use ${SITE_NAME} as a personalized workspace for your saved bills, members, committees, and daily brief.`,
        path: "/workspace",
        robots: NOINDEX_ROBOTS,
        title: `Workspace | ${SITE_NAME}`,
      };
    case "account-settings":
      return {
        description: `Manage your ${SITE_NAME} account profile, email, and password.`,
        path: "/settings/account",
        robots: NOINDEX_ROBOTS,
        title: `Settings | ${SITE_NAME}`,
      };
    case "notifications-settings":
      return {
        description: `Choose email alerts and a daily brief for the bills, members, and committees you follow on ${SITE_NAME}.`,
        path: "/settings/notifications",
        robots: NOINDEX_ROBOTS,
        title: `Notifications | ${SITE_NAME}`,
      };
    case "sign-in":
      return {
        description: `Sign in to ${SITE_NAME} to access personalized congressional tracking features.`,
        path: "/sign-in",
        robots: NOINDEX_ROBOTS,
        title: `Sign in | ${SITE_NAME}`,
      };
    case "sign-up":
      return {
        description: `Create a ${SITE_NAME} account for personalized congressional tracking features.`,
        path: "/sign-up",
        robots: NOINDEX_ROBOTS,
        title: `Sign up | ${SITE_NAME}`,
      };
    case "forgot-password":
      return {
        description: `Request a password reset link for your ${SITE_NAME} account.`,
        path: "/forgot-password",
        robots: NOINDEX_ROBOTS,
        title: `Reset password | ${SITE_NAME}`,
      };
    case "reset-password":
      return {
        description: `Set a new password for your ${SITE_NAME} account.`,
        path: "/reset-password",
        robots: NOINDEX_ROBOTS,
        title: `Set new password | ${SITE_NAME}`,
      };
    case "welcome":
      return {
        description: `Finish setting up your ${SITE_NAME} account profile.`,
        path: "/welcome",
        robots: NOINDEX_ROBOTS,
        title: `Welcome | ${SITE_NAME}`,
      };
    case "welcome-access":
      return {
        description: `Choose your ${SITE_NAME} account access level.`,
        path: "/welcome/access",
        robots: NOINDEX_ROBOTS,
        title: `Choose access | ${SITE_NAME}`,
      };
    case "bills":
      return buildListSeoDescriptor(
        "/bills",
        "Bills in Congress",
        route.search,
      );
    case "members":
      return buildListSeoDescriptor(
        "/members",
        "Members of Congress",
        route.search,
      );
    case "schedule":
      return buildListSeoDescriptor(
        "/schedule",
        "Committee activity",
        route.search,
      );
    case "committee-detail":
      return buildCommitteeDetailSeo(route);
    case "fec-committee-detail":
      return buildFecCommitteeDetailSeo(route);
    case "bill-detail":
      return buildBillDetailSeo(route);
    case "bill-vote-detail":
      return buildBillVoteDetailSeo(route);
    case "house-vote-detail":
      return buildHouseVoteDetailSeo(route);
    case "member-detail":
      return buildMemberDetailSeo(route);
    case "meeting-detail":
      return buildMeetingDetailSeo(route);
    case "senate-vote-detail":
      return buildSenateVoteDetailSeo(route);
    case "fallback":
      return {
        description: DEFAULT_DESCRIPTION,
        path: route.pathname ?? "/",
        robots: NOINDEX_ROBOTS,
        title: `Page not found | ${SITE_NAME}`,
      };
  }
}

function buildListSeoDescriptor(
  path: string,
  label: string,
  search?: string,
): BaseSeoDescriptor {
  const hasSearch = hasNonEmptySearch(search);

  return {
    description: `${label}. Browse public congressional records and summaries with ${SITE_NAME}.`,
    path,
    robots: hasSearch ? NOINDEX_ROBOTS : INDEX_ROBOTS,
    title: `${label} | ${SITE_NAME}`,
  };
}

function buildBillDetailSeo(
  route: Extract<SeoRoute, { kind: "bill-detail" }>,
): BaseSeoDescriptor {
  const bill = route.bill;
  const path = bill
    ? `/bills/${bill.congress}/${bill.billType}/${bill.billNumber}`
    : route.congress && route.billType && route.billNumber
      ? `/bills/${route.congress}/${route.billType}/${route.billNumber}`
      : "/bills";
  const title = bill
    ? `${bill.citation}: ${bill.title} | ${SITE_NAME}`
    : `Bill details | ${SITE_NAME}`;
  const fallbackDescription = bill
    ? stripHtml(bill.summary?.textHtml ?? "") ||
      `View the latest official status and public summary for ${bill.citation} on ${SITE_NAME}.`
    : "Browse the latest official status, summary text, and committee activity for bills in Congress.";

  return {
    description: bill?.latestActionText ?? fallbackDescription,
    path,
    robots: bill ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    structuredData: bill
      ? (context) => buildBillDetailStructuredData(context, bill)
      : null,
    title,
  };
}

function buildBillVoteDetailSeo(
  route: Extract<SeoRoute, { kind: "bill-vote-detail" }>,
): BaseSeoDescriptor {
  const bill = route.bill;
  const vote = route.vote;
  const path = bill
    ? route.voteId
      ? `/bills/${bill.congress}/${bill.billType}/${bill.billNumber}/votes/${encodeURIComponent(route.voteId)}`
      : `/bills/${bill.congress}/${bill.billType}/${bill.billNumber}`
    : route.congress && route.billType && route.billNumber && route.voteId
      ? `/bills/${route.congress}/${route.billType}/${route.billNumber}/votes/${encodeURIComponent(route.voteId)}`
      : "/bills";
  const voteHeadline = getVoteSeoHeadline(vote);
  const title = bill
    ? `${bill.citation} vote${voteHeadline ? `: ${voteHeadline}` : ""} | ${SITE_NAME}`
    : `Bill vote details | ${SITE_NAME}`;

  return {
    description:
      bill && vote
        ? `${vote.result ?? "Recorded vote"} · ${formatSeoDateTime(vote.voteDate)} · ${bill.citation}.`
        : bill
          ? `Public vote result, totals, and party breakdown for ${bill.citation} on ${SITE_NAME}.`
          : `Browse vote results, totals, and party breakdowns for congressional bill votes on ${SITE_NAME}.`,
    path,
    robots: bill && vote ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    title,
  };
}

function buildCommitteeDetailSeo(
  route: Extract<SeoRoute, { kind: "committee-detail" }>,
): BaseSeoDescriptor {
  const committee = route.committee;
  const normalizedChamber = normalizeCommitteeChamber(
    committee?.chamber ?? route.chamber,
  );
  const path = committee
    ? `/committees/${committee.congress}/${normalizedChamber}/${committee.systemCode}`
    : route.congress && normalizedChamber && route.committeeCode
      ? `/committees/${route.congress}/${normalizedChamber}/${route.committeeCode}`
      : "/schedule";

  return {
    description: committee
      ? `${formatCommitteeDisplayName(committee.name)}. ${committee.memberCount} members, ${committee.billCount} bills, ${committee.upcomingMeetingCount} upcoming meetings, and ${committee.subcommitteeCount} subcommittees tracked on ${SITE_NAME}.`
      : `Browse committee leadership, referred legislation, and hearing summaries on ${SITE_NAME}.`,
    path,
    robots: committee ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    title: committee
      ? `${formatCommitteeDisplayName(committee.name)} | ${SITE_NAME}`
      : `Committee details | ${SITE_NAME}`,
  };
}

function buildFecCommitteeDetailSeo(
  route: Extract<SeoRoute, { kind: "fec-committee-detail" }>,
): BaseSeoDescriptor {
  const committee = route.committee;
  const committeeId = committee?.committeeId ?? route.committeeId;
  return {
    description: committee
      ? `${committee.committeeLabel}. Review current-cycle outside spending, filings, funding aggregates, and monitor activity on ${SITE_NAME}.`
      : `Review current-cycle FEC committee spending, filings, and monitor activity on ${SITE_NAME}.`,
    path: committeeId
      ? `/fec/committees/${encodeURIComponent(committeeId)}`
      : "/members",
    robots: NOINDEX_ROBOTS,
    title: committee
      ? `${committee.name} | ${SITE_NAME}`
      : `FEC committee profile | ${SITE_NAME}`,
  };
}

function buildMemberDetailSeo(
  route: Extract<SeoRoute, { kind: "member-detail" }>,
): BaseSeoDescriptor {
  const member = route.member;
  const bioguideId = member?.bioguideId ?? route.bioguideId;
  const isCurrentCongress =
    route.congress !== undefined &&
    route.currentCongress !== undefined &&
    String(route.congress) === String(route.currentCongress);
  const path = bioguideId
    ? `/members/${bioguideId}${route.congress && !isCurrentCongress ? `?congress=${route.congress}` : ""}`
    : "/members";
  const title = member
    ? `${member.displayName} | ${SITE_NAME}`
    : `Member profile | ${SITE_NAME}`;

  return {
    description: member
      ? `${describeMemberRole(member)}. View terms, committee assignments, sponsored bills, and headline campaign finance data on ${SITE_NAME}.`
      : `Browse public member profiles, sponsored bills, and headline campaign finance data on ${SITE_NAME}.`,
    path,
    robots: member ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    structuredData: member
      ? (context) => buildMemberDetailStructuredData(context, member)
      : null,
    title,
  };
}

function buildMeetingDetailSeo(
  route: Extract<SeoRoute, { kind: "meeting-detail" }>,
): BaseSeoDescriptor {
  const meeting = route.meeting;
  const normalizedChamber = normalizeMeetingChamber(
    meeting?.chamber ?? route.chamber,
  );
  const path = meeting
    ? `/schedule/${meeting.congress}/${normalizedChamber}/${getMeetingEventId(meeting.meetingId)}`
    : route.congress && normalizedChamber && route.eventId
      ? `/schedule/${route.congress}/${normalizedChamber}/${route.eventId}`
      : "/schedule";
  const title = meeting
    ? `${meeting.title} | ${SITE_NAME}`
    : `Committee meeting details | ${SITE_NAME}`;

  return {
    description: meeting
      ? `${meeting.meetingStatus} · ${formatSeoDateTime(meeting.meetingDate)} · ${formatCommitteeList(meeting.committees)}.`
      : `Browse public committee hearing schedules, meeting documents, witnesses, and related legislation on ${SITE_NAME}.`,
    path,
    robots: meeting ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    title,
  };
}

function buildSenateVoteDetailSeo(
  route: Extract<SeoRoute, { kind: "senate-vote-detail" }>,
): BaseSeoDescriptor {
  const senateVote = route.senateVote;
  const vote = route.vote;
  const path =
    senateVote || (route.congress && route.sessionNumber && route.voteNumber)
      ? `/votes/senate/${senateVote?.congress ?? route.congress}/${senateVote?.sessionNumber ?? route.sessionNumber}/${senateVote?.voteNumber ?? route.voteNumber}`
      : "/schedule";
  const voteLabel =
    senateVote?.bill?.billCitation ??
    (senateVote ? `Senate Vote ${senateVote.voteNumber}` : "Senate vote");
  const reference = senateVote
    ? resolveSenateFloorVoteReference(senateVote)
    : null;
  const referenceText = reference
    ? formatSenateFloorVoteReference(reference)
    : null;
  const subjectText = reference?.title ?? referenceText ?? senateVote?.issue;

  return {
    description:
      senateVote && vote
        ? `${vote.result ?? "Recorded vote"} · ${formatSeoDateTime(vote.voteDate)} · ${subjectText}.`
        : senateVote
          ? `${subjectText} · View the public roll call result and party totals on ${SITE_NAME}.`
          : `Browse public Senate roll call results, totals, and party breakdowns on ${SITE_NAME}.`,
    path,
    robots: senateVote && vote ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    title: `${voteLabel} | ${SITE_NAME}`,
  };
}

function buildHouseVoteDetailSeo(
  route: Extract<SeoRoute, { kind: "house-vote-detail" }>,
): BaseSeoDescriptor {
  const houseVote = route.houseVote;
  const vote = route.vote;
  const path =
    houseVote || (route.congress && route.sessionNumber && route.rollNumber)
      ? `/votes/house/${houseVote?.congress ?? route.congress}/${houseVote?.sessionNumber ?? route.sessionNumber}/${houseVote?.rollNumber ?? route.rollNumber}`
      : "/members";
  const voteLabel =
    houseVote?.linkedBill?.citation ??
    houseVote?.citation ??
    (houseVote ? `House Roll ${houseVote.rollNumber}` : "House vote");

  return {
    description:
      houseVote && vote
        ? `${vote.result ?? "Recorded vote"} · ${formatSeoDateTime(vote.voteDate)} · ${getVoteHeadlineForSeo(vote, houseVote.rollNumber)}.`
        : houseVote
          ? `View public House roll call results, totals, and party breakdowns on ${SITE_NAME}.`
          : `Browse public House roll call results, totals, and party breakdowns on ${SITE_NAME}.`,
    path,
    robots: houseVote && vote ? INDEX_ROBOTS : NOINDEX_ROBOTS,
    title: `${voteLabel} | ${SITE_NAME}`,
  };
}

function getVoteHeadlineForSeo(vote: VoteSeoInput, rollNumber: number): string {
  return vote.question ?? vote.title ?? `House Roll ${rollNumber}`;
}

function createVoteSocialCard({
  billLabel,
  fallbackDate,
  fallbackResult,
  subject,
  vote,
}: {
  billLabel: string;
  fallbackDate?: string | null;
  fallbackResult?: string | null;
  subject: string;
  vote: VoteSeoInput | null;
}): SocialCardDescriptor {
  const result = vote?.result ?? fallbackResult ?? "Recorded vote";
  const voteDate = vote?.voteDate ?? fallbackDate ?? null;
  const tally = vote
    ? {
        yea: vote.yeaCount,
        nay: vote.nayCount,
        present: vote.presentCount,
        other: vote.otherCount,
        otherLabel: vote.otherLabel ?? "Other",
      }
    : undefined;

  return {
    badge: result,
    detailLine: vote ? formatVotePartySocialLine(vote) : undefined,
    eyebrow: [vote?.chamber, voteDate ? formatSeoDateTime(voteDate) : null]
      .filter(Boolean)
      .join(" · "),
    headline: billLabel,
    imageAlt: `Govoptes vote card for ${billLabel}`,
    metrics: tally
      ? [
          { label: "Yea", value: formatSocialVoteCount(tally.yea) },
          { label: "Nay", value: formatSocialVoteCount(tally.nay) },
          { label: "Present", value: formatSocialVoteCount(tally.present) },
          {
            label: tally.otherLabel,
            value: formatSocialVoteCount(tally.other),
          },
        ]
      : undefined,
    supportingLine: clamp(subject, 110),
    tally,
    variant: "vote",
  };
}

function formatBillSponsorLine(bill: BillSeoInput): string | undefined {
  const sponsor = bill.sponsorName?.trim();
  if (!sponsor) {
    return undefined;
  }

  return `Sponsored by ${sponsor}`;
}

function formatSocialVoteCount(value: number | null | undefined): string {
  return typeof value === "number" ? value.toLocaleString() : "N/A";
}

function formatVotePartySocialLine(vote: VoteSeoInput): string | undefined {
  const parties = vote.partyBreakdown
    .filter((party) => party.totalCount > 0)
    .sort((left, right) => right.totalCount - left.totalCount)
    .slice(0, 2);

  if (parties.length === 0) {
    return undefined;
  }

  return parties
    .map((party) => {
      const label = party.partyName ?? party.partyCode ?? "Unknown";
      return `${label}: ${party.yeaCount} yea, ${party.nayCount} nay`;
    })
    .join(" · ");
}

function buildSocialImage(
  route: SeoRoute,
  path: string,
  siteUrl: string,
): { height: number; type: "image/jpeg" | "image/png"; url: string; width: number } {
  if (
    route.kind === "member-detail" &&
    route.member?.portraitUrl &&
    /^[A-Z]\d{6}$/i.test(route.member.bioguideId)
  ) {
    const url = new URL(
      `${MEMBER_PORTRAIT_SOCIAL_IMAGE_PATH}/${route.member.bioguideId.toUpperCase()}.jpg`,
      `${siteUrl}/`,
    );
    return { height: 550, type: "image/jpeg", url: url.toString(), width: 450 };
  }

  const url = new URL(SOCIAL_IMAGE_PATH, `${siteUrl}/`);
  // Raster social images are broadly supported by link-preview crawlers. Keep
  // the route argument in this helper so the dynamic renderer can be restored
  // once it has a raster output, but do not create cache-fragmenting query URLs
  // for the shared static PNG.
  if (SOCIAL_IMAGE_PATH.startsWith("/_social/")) {
    url.searchParams.set("path", path);
  }
  return { height: 630, type: "image/png", url: url.toString(), width: 1200 };
}

function createDefaultSocialCard(
  variant: SocialCardVariant,
): SocialCardDescriptor {
  return {
    headline: DEFAULT_SOCIAL_HEADLINE,
    imageAlt: `Govoptes share card with the tagline ${DEFAULT_SOCIAL_HEADLINE}`,
    supportingLine: DEFAULT_SOCIAL_SUPPORTING_LINE,
    variant,
  };
}

function formatMemberSocialSupportingLine(member: MemberSeoInput): string {
  const party = formatParty(member.partyCode);
  const chamber = member.chamber === "Senate" ? "Senator" : "Representative";
  const district = member.district ? `-${member.district}` : "";
  return `${chamber} ${party ? `${party} ` : ""}for ${member.stateCode}${district}`
    .replace(/\s+/g, " ")
    .trim();
}

function formatCommitteeSocialSupportingLine(
  committee: CommitteeSeoInput,
): string {
  return `${committee.memberCount} members, ${committee.billCount} bills, and ${committee.upcomingMeetingCount} upcoming meetings tracked.`;
}

function buildStructuredData(
  siteUrl: string,
  extraGraph: Record<string, unknown>[] = [],
): Record<string, unknown> {
  const rootUrl = buildRootUrl(siteUrl);
  const logoUrl = new URL("/govoptes-mark.svg", `${siteUrl}/`).toString();

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": buildWebsiteId(siteUrl),
        alternateName: SITE_ALTERNATE_NAME,
        description: DEFAULT_DESCRIPTION,
        name: SITE_NAME,
        url: rootUrl,
      },
      {
        "@type": "Organization",
        "@id": buildOrganizationId(siteUrl),
        alternateName: SITE_ALTERNATE_NAME,
        description: DEFAULT_DESCRIPTION,
        logo: logoUrl,
        name: SITE_NAME,
        url: rootUrl,
      },
      ...extraGraph,
    ],
  };
}

function resolveStructuredData(
  structuredData: BaseSeoDescriptor["structuredData"],
  context: StructuredDataContext,
): Record<string, unknown> | null {
  if (!structuredData) {
    return null;
  }

  return typeof structuredData === "function"
    ? structuredData(context)
    : structuredData;
}

function buildBillDetailStructuredData(
  context: StructuredDataContext,
  bill: BillSeoInput,
): Record<string, unknown> {
  const legislationId = `${context.canonicalUrl}#legislation`;
  const breadcrumbId = `${context.canonicalUrl}#breadcrumb`;
  const summaryText = stripHtml(bill.summary?.textHtml ?? "");
  const keywords = [bill.policyArea, ...bill.subjects].filter(isNonEmptyString);
  const legislationIdentifiers = [
    bill.citation,
    formatLawIdentifier(bill),
  ].filter(isNonEmptyString);

  return buildStructuredData(context.siteUrl, [
    buildWebPageStructuredData(context, legislationId, breadcrumbId),
    buildBreadcrumbStructuredData(breadcrumbId, [
      { name: SITE_NAME, url: buildRootUrl(context.siteUrl) },
      {
        name: "Bills",
        url: new URL("/bills", `${context.siteUrl}/`).toString(),
      },
      { name: bill.citation, url: context.canonicalUrl },
    ]),
    compactObject({
      "@type": "Legislation",
      "@id": legislationId,
      abstract: summaryText,
      alternateName: bill.citation,
      creativeWorkStatus: getBillStatusLabel(bill.status, bill.originChamber),
      dateCreated: bill.introducedDate,
      dateModified: bill.latestActionDate,
      description: context.description,
      identifier: compactObject({
        "@type": "PropertyValue",
        propertyID: "Govoptes bill ID",
        value: bill.billId,
      }),
      jurisdiction: "United States",
      keywords,
      legislationIdentifier: legislationIdentifiers,
      legislationJurisdiction: "United States",
      legislationType: bill.lawType ?? bill.billType.toUpperCase(),
      name: bill.title,
      sponsor: bill.sponsorName
        ? {
            "@type": "Person",
            name: bill.sponsorName,
          }
        : null,
      url: context.canonicalUrl,
    }),
  ]);
}

function buildMemberDetailStructuredData(
  context: StructuredDataContext,
  member: MemberSeoInput,
): Record<string, unknown> {
  const personId = `${context.canonicalUrl}#person`;
  const breadcrumbId = `${context.canonicalUrl}#breadcrumb`;

  return buildStructuredData(context.siteUrl, [
    buildWebPageStructuredData(context, personId, breadcrumbId),
    buildBreadcrumbStructuredData(breadcrumbId, [
      { name: SITE_NAME, url: buildRootUrl(context.siteUrl) },
      {
        name: "Members",
        url: new URL("/members", `${context.siteUrl}/`).toString(),
      },
      { name: member.displayName, url: context.canonicalUrl },
    ]),
    compactObject({
      "@type": "Person",
      "@id": personId,
      affiliation: {
        "@type": "Organization",
        name: "United States Congress",
      },
      description: context.description,
      identifier: compactObject({
        "@type": "PropertyValue",
        propertyID: "Bioguide ID",
        value: member.bioguideId,
      }),
      jobTitle:
        member.chamber === "Senate" ? "U.S. Senator" : "U.S. Representative",
      memberOf: {
        "@type": "Organization",
        name: "United States Congress",
      },
      name: member.displayName,
      sameAs: member.officialWebsiteUrl,
      url: context.canonicalUrl,
    }),
  ]);
}

function buildWebPageStructuredData(
  context: StructuredDataContext,
  entityId: string,
  breadcrumbId: string,
): Record<string, unknown> {
  return {
    "@type": "WebPage",
    "@id": `${context.canonicalUrl}#webpage`,
    about: { "@id": entityId },
    breadcrumb: { "@id": breadcrumbId },
    description: context.description,
    isPartOf: { "@id": buildWebsiteId(context.siteUrl) },
    name: context.title,
    url: context.canonicalUrl,
  };
}

function buildBreadcrumbStructuredData(
  breadcrumbId: string,
  items: Array<{ name: string; url: string }>,
): Record<string, unknown> {
  return {
    "@type": "BreadcrumbList",
    "@id": breadcrumbId,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      item: item.url,
      name: item.name,
      position: index + 1,
    })),
  };
}

function buildRootUrl(siteUrl: string): string {
  return new URL("/", `${siteUrl}/`).toString();
}

function buildWebsiteId(siteUrl: string): string {
  return `${buildRootUrl(siteUrl)}#website`;
}

function buildOrganizationId(siteUrl: string): string {
  return `${buildRootUrl(siteUrl)}#organization`;
}

function compactObject(
  input: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => {
      if (value === null || typeof value === "undefined") {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return typeof value !== "string" || value.trim().length > 0;
    }),
  );
}

function formatLawIdentifier(bill: BillSeoInput): string | null {
  if (!bill.lawNumber) {
    return null;
  }

  return bill.lawType ? `${bill.lawType} ${bill.lawNumber}` : bill.lawNumber;
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value?.trim());
}

function normalizeBaseUrl(input: string): string {
  const url = new URL(input);
  url.pathname = "/";
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

function clamp(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function hasNonEmptySearch(search?: string): boolean {
  if (!search) {
    return false;
  }

  return [...new URLSearchParams(search).entries()].some(
    ([, value]) => value.trim().length > 0,
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function getVoteSeoHeadline(
  vote: VoteSeoInput | null | undefined,
): string | null {
  const headline = vote?.question?.trim() || vote?.title?.trim() || "";
  return headline || null;
}

function describeMemberRole(member: MemberSeoInput): string {
  const party = formatParty(member.partyCode);
  const seat = formatDistrict(member.stateCode, member.district);
  const role = [member.chamber, party].filter(Boolean).join(" ");

  if (seat === "Unknown seat") {
    return role || "Congressional member";
  }

  return `${role || "Congressional member"} representing ${seat}`;
}

function formatParty(partyCode: string | null | undefined): string {
  switch ((partyCode ?? "").toUpperCase()) {
    case "D":
      return "Democrat";
    case "R":
      return "Republican";
    case "I":
      return "Independent";
    default:
      return "";
  }
}

function formatDistrict(
  state: string | null | undefined,
  district: string | null | undefined,
): string {
  if (!state) {
    return "Unknown seat";
  }

  if (!district || district === "0" || district.toLowerCase() === "null") {
    return state;
  }

  return `${state}-${district}`;
}

function formatCommitteeList(committees: string[]): string {
  const visibleCommittees = committees.filter(Boolean);
  if (visibleCommittees.length === 0) {
    return "Committee TBA";
  }

  if (visibleCommittees.length === 1) {
    return visibleCommittees[0]!;
  }

  if (visibleCommittees.length === 2) {
    return `${visibleCommittees[0]} and ${visibleCommittees[1]}`;
  }

  return `${visibleCommittees[0]} and ${visibleCommittees.length - 1} more committees`;
}

function formatSeoDateTime(value: string | null | undefined): string {
  if (!value) {
    return "Date TBA";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function normalizeMeetingChamber(value: string | null | undefined): string {
  switch ((value ?? "").toLowerCase()) {
    case "house":
      return "house";
    case "senate":
      return "senate";
    case "nochamber":
      return "nochamber";
    default:
      return "";
  }
}

function normalizeCommitteeChamber(value: string | null | undefined): string {
  return normalizeCommitteeRouteChamber(value);
}

function formatCommitteeDisplayName(name: string): string {
  return name
    .replace(/^Committee on the\s+/i, "")
    .replace(/^Committee on\s+/i, "");
}

function getMeetingEventId(meetingId: string): string {
  return meetingId.split("-").at(-1) ?? meetingId;
}
